import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, defer } from 'rxjs';
import { distinctUntilChanged, finalize, map, tap } from 'rxjs/operators';
import { RESTService } from 'src/app/shared/services/rest.service';
import { RESTServiceV2 } from 'src/app/shared/services/rest.service.v2';

@Injectable({
  providedIn: 'root',
})
export class LaftService {
  private readonly pendingStepSaves$ = new BehaviorSubject<number>(0);

  // Cached because the route guard and the side menu answer off the declaration and
  // neither of them can await a request; `null` until the first fetch.
  private readonly declaration$ = new BehaviorSubject<any>(null);

  readonly isSavingStep$: Observable<boolean> = this.pendingStepSaves$.pipe(
    map(pending => pending > 0),
    distinctUntilChanged()
  );

  constructor(private rest: RESTService, private restV2: RESTServiceV2) {}

  private trackStepSave<T>(request: Observable<T>): Observable<T> {
    return defer(() => {
      this.pendingStepSaves$.next(this.pendingStepSaves$.value + 1);

      return request.pipe(
        finalize(() => this.pendingStepSaves$.next(Math.max(0, this.pendingStepSaves$.value - 1)))
      );
    });
  }

  createLaftDeclaration(data: any): Observable<any> {
    return this.rest.post(`/laft-declarations`, JSON.stringify(data));
  }

  createLegalLaftDeclaration(data: any): Observable<any> {
    return this.rest.post(`/laft-declarations-pj`, JSON.stringify(data));
  }

  saveLegalLaftDeclaration(data: any): Observable<any> {
    return this.rest.post('/laft-declarations-pj/save', data);
  }

  save(data: any): Observable<any> {
    return this.rest.post('/laft-declarations/save', data);
  }

  get currentDeclaration(): any {
    return this.declaration$.value;
  }

  getData(): Observable<any> {
    return this.rest
      .get('/laft-declarations')
      .pipe(tap((data: any) => this.declaration$.next(data?.LAFTData ?? null)));
  }

  getClientData(userId?: string): Observable<any> {
    const resource = userId
      ? `/laft/declarations/${encodeURIComponent(userId)}`
      : '/laft/declarations';
    return this.restV2.get(resource);
  }

  verifyTaxReport(data: any): Observable<any> {
    return this.rest.post('/laft-declarations/tax-report', data);
  }

  /**
   * FIN-1710 (D88) · mint the Keynua signature on demand, when a pre-approved
   * inversionista presses *Firmar mi contrato*.
   *
   * Under `laft-pn-v2` their submit sends no documents and creates no signature request,
   * so this is the only route by which they ever get a token. The case is idempotent: a
   * declaration that already carries one answers with its URL instead of opening a second
   * contract.
   */
  requestSignature(): Observable<any> {
    return this.rest.post('/laft-declarations/request-signature', {});
  }

  /**
   * Mark Keynua widget as completed by user
   * Called when the user completes the signature process in the Keynua widget
   */
  markKeynuaWidgetCompleted(): Observable<any> {
    return this.rest.post('/keynua/widget/completed', {});
  }

  /**
   * Log Keynua widget drop-off (persona natural): user closed the widget with "X" without completing the signing process.
   * Creates an audit log in the backend.
   */
  logKeynuaWidgetDropOff(): Observable<any> {
    return this.rest.post('/keynua/widget/drop-off', {});
  }

  updatePersonalData(laftId: string, data: any): Observable<any> {
    return this.trackStepSave(this.restV2.patch(`/laft/${laftId}/personal-data`, data));
  }

  updateCompanyBeneficiary(laftId: string, body: any): Observable<unknown> {
    return this.trackStepSave(this.restV2.patch(`/laft/${laftId}/company-beneficiary`, body));
  }

  validateTaxReport(taxReport: any): Observable<any> {
    return this.restV2.post('/laft/tax-report', { taxReport });
  }

  validateCertifiedCopy(certifiedCopy: string): Observable<any> {
    return this.restV2.post('/laft/validate-certified-copy', { certifiedCopy });
  }

  /**
   * Checks the uploaded ID copy against the number declared on step 1. The number itself
   * is never sent: the server reads it off the account, so the caller cannot supply both
   * sides of the comparison.
   *
   * The answer is deliberately a bare boolean — the reason a document was rejected is not
   * disclosed to the browser.
   */
  validateIdCopy(idFrontPicture: string): Observable<{ isValid: boolean }> {
    return this.rest.post('/ai/id-validation', { idFrontPicture }) as Observable<{ isValid: boolean }>;
  }

  updatePepData(laftId: string, data: any): Observable<any> {
    return this.trackStepSave(this.restV2.patch(`/laft/${laftId}/pep-data`, data));
  }

  updatePendingPepData(laftId: string, body: any): Observable<any> {
    return this.trackStepSave(this.restV2.patch(`/laft/${laftId}/pending-pep-data`, body));
  }

  updateInvestigationData(laftId: string, body: any): Observable<any> {
    return this.restV2.patch(`/laft/${laftId}/investigation-data`, body);
  }

  requestPendingContractsSignature(): Observable<{ ok: boolean; revalidationRequested?: boolean }> {
    return this.restV2.post('/laft/signature/pending-contracts', {}) as Observable<{
      ok: boolean;
      revalidationRequested?: boolean;
    }>;
  }

  logLaftDeclarationAction(
    action: string,
    actionDescription: string,
    laftDeclarationId?: string | null
  ): void {
    const payload: Record<string, string> = {
      action,
      actionDescription,
    };
    if (laftDeclarationId) {
      payload.laftDeclarationId = laftDeclarationId;
    }
    this.rest
      .post('/laft-declaration-history', payload)
      .subscribe({
        error: (err) => console.warn('Failed to log LAFT action:', err),
      });
  }
}
